// 서버의 검증된 키만 문장으로 옮긴다. 손상된 응답과 유효한 빈 목록을 구분한다. //
export function parseProgress(progressJson) {
  if (typeof progressJson !== 'string') return null;

  try {
    const items = JSON.parse(progressJson);

    if (!Array.isArray(items) || items.length > 10) return null;
    if (items.some(item => !item || !/^[a-z][a-z_]{0,19}$/.test(item.key)
      || !['run', 'done'].includes(item.state)
      || (item.domains !== undefined && (!Array.isArray(item.domains) || item.domains.length > 12
        || item.domains.some(domain => typeof domain !== 'string' || !/^[a-z][a-z_]{0,19}$/.test(domain)))))) return null;

    return items;
  }
  catch {
    return null;
  }
}

const LINE_TEXT = {
  triage: ['질문을 살펴보고 있어요', 'Reviewing your question', '質問を確認しています'],
  plan: ['필요한 기록을 정리하고 있어요', 'Planning which records to check', '必要な記録を整理しています'],
  prepare: ['기록을 확인할 준비를 하고 있어요', 'Preparing to check the records', '記録を確認する準備をしています'],
  period: ['요청하신 기간을 확인하고 있어요', 'Checking the requested period', 'ご指定の期間を確認しています'],
  compare: ['비교 기준을 정리하고 있어요', 'Preparing the comparison', '比較の基準を整理しています'],
  retry: ['확인 방법을 다시 정리하고 있어요', 'Revising how to check the records', '確認方法を見直しています'],
  replan: ['다른 확인 방법을 찾고 있어요', 'Finding another way to check', '別の確認方法を探しています'],
  query: ['매장 기록을 확인하고 있어요', 'Checking store records', '店舗の記録を確認しています'],
  answer: ['답을 준비하고 있어요', 'Preparing your answer', '回答を準備しています'],
  reply: ['답변을 정리하고 있어요', 'Putting your answer together', '回答をまとめています'],
  guide_find: ['사용 방법을 찾아보고 있어요', 'Looking up the instructions', '使い方を調べています'],
  fallback: ['다른 방식으로 답을 준비하고 있어요', 'Preparing your answer another way', '別の方法で回答を準備しています'],
  reuse: ['앞서 확인한 기록을 다시 살펴보고 있어요', 'Reviewing records already checked', '確認済みの記録を見直しています'],
  advice: ['확인한 기록을 바탕으로 운영 제안을 정리하고 있어요', 'Preparing suggestions based on the records', '確認した記録をもとに提案をまとめています'],
  sms_target: ['문자를 보낼 대상을 확인할 준비를 하고 있어요', 'Preparing to check the message recipients', 'メッセージの送信対象を確認する準備をしています'],
  m_ltv: ['회원 생애 가치를 계산하고 있어요', 'Calculating member lifetime value', '会員の生涯価値を計算しています'],
  fu_sort: ['앞서 본 결과의 순서를 정리하고 있어요', 'Sorting the previous results', '前の結果の順序を整理しています'],
  fu_top: ['앞서 본 결과에서 요청한 순위를 추리고 있어요', 'Selecting the requested ranks', '前の結果からご指定の順位を選んでいます'],
  fu_period: ['바꿔 요청하신 기간을 확인하고 있어요', 'Checking the updated period', '変更された期間を確認しています'],
  fu_people: ['앞서 말씀드린 대상을 확인하고 있어요', 'Checking the previously mentioned people', '先ほどの対象を確認しています'],
  fu_compare: ['앞서 본 결과의 비교 기준을 정리하고 있어요', 'Preparing to compare the previous results', '前の結果の比較基準を整理しています'],
  fu_compare_two: ['요청하신 두 기간의 비교 기준을 정리하고 있어요', 'Preparing to compare the two periods', 'ご指定の二つの期間の比較基準を整理しています'],
  fu_redo: ['바꿔 요청하신 조건을 확인하고 있어요', 'Checking the updated conditions', '変更された条件を確認しています'],
  fu_count: ['앞서 본 대상의 수를 확인하고 있어요', 'Checking the number of previous results', '前の対象の件数を確認しています'],
  fu_recheck: ['앞서 말씀드린 수치를 다시 확인하고 있어요', 'Rechecking the previous figures', '先ほどの数値を再確認しています'],
  fu_agree: ['앞서 제안한 작업을 준비하고 있어요', 'Preparing the previously suggested task', '先ほど提案した作業を準備しています'],
};

const DOMAIN_TEXT = {
  sales: ['매출 기록', 'sales records', '売上記録'],
  member: ['회원 기록', 'member records', '会員記録'],
  membership: ['회원권 기록', 'membership records', '会員権の記録'],
  attendance: ['출석 기록', 'attendance records', '出席記録'],
  reservation: ['예약 기록', 'reservation records', '予約記録'],
  class: ['수업 기록', 'class records', 'レッスン記録'],
  guest: ['방문자 기록', 'guest records', '来店者の記録'],
  coach: ['코치 기록', 'coach records', 'コーチの記録'],
  point: ['포인트 기록', 'point records', 'ポイント記録'],
  message: ['발송 기록', 'message records', '送信記録'],
  product: ['상품 기록', 'product records', '商品記録'],
  locker: ['락커 기록', 'locker records', 'ロッカー記録'],
  notice: ['공지 기록', 'notice records', 'お知らせの記録'],
  wod: ['운동 기록', 'workout records', '運動記録'],
  partner: ['제휴 기록', 'partner records', '提携記録'],
  store: ['매장 설정', 'store settings', '店舗の設定'],
};

const TOPIC_TEXT = {
  revenue: ['매출', 'revenue', '売上'],
  attendance: ['출석', 'attendance', '出席'],
  members: ['회원 변화', 'member changes', '会員の変化'],
  churn: ['회원 이탈', 'member churn', '会員の離脱'],
  schedule: ['수업 운영', 'class scheduling', 'レッスン運営'],
  overall: ['매장 운영', 'store operations', '店舗運営'],
};

// 끝난 예고와 알 수 없는 최신 단계는 이전 계산 문구를 다시 살리지 않는다. //
export function progressLine(items, language = 'ko', initialText = null) {
  const index = language === 'en' ? 1 : language === 'ja' ? 2 : 0;
  const generic = LINE_TEXT.answer[index];

  if (items === null || items === undefined) return initialText || generic;

  const current = [...items].reverse().find(item => item.state === 'run');

  if (!current || !LINE_TEXT[current.key]) return generic;

  const tags = current.domains || [];
  const direct = tags.find(tag => LINE_TEXT[tag]);

  if (direct) return LINE_TEXT[direct][index];

  const topic = tags.find(tag => /^t_(revenue|attendance|members|churn|schedule|overall)_(why|trend|compare|segment|design)$/.test(tag));

  if (topic && current.key !== 'answer') {
    const [, metric, branch] = topic.match(/^t_(.+)_(why|trend|compare|segment|design)$/);
    const label = TOPIC_TEXT[metric][index];

    if (branch === 'design') return [`${label} 개선 방안을 정리하고 있어요`, `Preparing ways to improve ${label}`, `${label}の改善案を整理しています`][index];
    return [`${label} 기록을 살펴보고 있어요`, `Reviewing ${label} records`, `${label}の記録を確認しています`][index];
  }

  if (current.key === 'query') {
    const names = tags.filter(tag => DOMAIN_TEXT[tag]).slice(0, 2).map(tag => DOMAIN_TEXT[tag][index]);

    if (names.length) return [`${names.join('과 ')}을 확인하고 있어요`, `Checking ${names.join(' and ')}`, `${names.join('と')}を確認しています`][index];
  }

  return LINE_TEXT[current.key][index];
}

// 서버 예고를 한 번도 받지 못한 동안에만 시간 안내를 쓴다. //
export function initialProgressLine(language, elapsedMs) {
  const index = language === 'en' ? 1 : language === 'ja' ? 2 : 0;

  if (elapsedMs >= 30000) return ['평소보다 오래 걸리고 있어요. 잠시만 기다려 주세요', 'Taking longer than usual. Please hold on', 'いつもより時間がかかっています。少々お待ちください'][index];
  if (elapsedMs >= 8000) return ['시간이 조금 걸리고 있어요', 'This is taking a little longer', '少し時間がかかっています'][index];

  return ['질문을 살펴보고 있어요', 'Reviewing your question', '質問を確認しています'][index];
}
